import type { Action } from '@atlassian/react-sweet-state';
import type { Environment } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import type { ContainerProps, State } from '../types.tsx';
import {
	deleteIssueProperties,
	deleteRule,
	disableRule,
} from '../../services/set-non-recurring.tsx';

export type SetAsNonRecurringIssueParams = {
	environment: Environment | null;
	cloudId: string;
	projectId: string;
	isProjectAdmin: boolean;
	isRuleOwner: boolean;
	isSiteAdmin: boolean;
};

export const setAsNonRecurringIssue =
	({
		environment,
		cloudId,
		projectId,
		isProjectAdmin,
		isRuleOwner,
		isSiteAdmin,
	}: SetAsNonRecurringIssueParams): Action<State, ContainerProps> =>
	async ({ setState, getState }, { issueKey }) => {
		const { issueProperties, errors } = getState();
		// this can't be !automationRuleUuid because recur-on-complete rules won't have an
		// automationRuleUuid, but they will have issueProperties
		if (!issueProperties) {
			return;
		}

		setState({
			...getState(),
			isDeletingRule: true,
		});

		if (!isSiteAdmin && !isProjectAdmin && !isRuleOwner) {
			setState({
				...getState(),
				isDeletingRule: false,
				// TODO: decide if we want to be appending to this list or overwriting it
				errors: [
					...(errors ?? []),
					new Error('User does not have permission to set issue as non-recurring'),
				],
			});
			return;
		}

		const { automationRuleUuid: ruleUuid } = issueProperties;
		if (ruleUuid) {
			try {
				if (isRuleOwner || isSiteAdmin) {
					await deleteRule({ environment, cloudId, projectId, ruleUuid });
				} else {
					await disableRule({ environment, cloudId, projectId, ruleUuid });
				}
			} catch (error: unknown) {
				// abort if the rule cannot be deleted/disabled
				setState({
					...getState(),
					isDeletingRule: false,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					errors: [...(errors ?? []), error as Error],
				});
				return;
			}
		}
		try {
			await deleteIssueProperties({ issueKey });
			setState({
				...getState(),
				isDeletingRule: false,
				issueProperties: null,
				ruleSummary: null,
			});
		} catch (error: unknown) {
			setState({
				...getState(),
				isDeletingRule: false,
				ruleSummary: null,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				errors: [...(errors ?? []), error as Error],
			});
		}
	};
